import {
  CONVERSATION,
  CONVERSATION_ITEM,
  CONVERSATION_ITEM_ATTACHMENTS,
  CONVERSATION_ITEM_LEAVE,
  CONVERSATION_ITEM_MEMBER,
  SEARCH_CONVERSATIONS,
  SEARCH_MESSAGES
} from '@/config/api/api-routes';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import type { MemberAddResponse } from '@/features/conversation/models/member-dto';
import type { AttachmentType } from '@/features/messages';
import { http } from '@/shared/api/http';
import type { ResultApi } from '@/shared/types/result-api';
import { uploadAsync } from '@/shared/utilities';
import type { Conversation } from '../models/conversation';
import type {
  ConversationCreate,
  ConversationListResponse,
  ConversationSearchResponse,
  ConversationsParams,
  UpdateConversationRes
} from '../models/conversation-dto';
import type { MessageSearchParams, MessageSearchResponse } from '../models/message-search-dto';
import { getUserByIds } from './member.service';

// Attachments DTOs
export interface AttachmentWrapperDto {
  messageId: string;
  bucketId?: string;
  timestamp: string;
  attachment?: {
    contentType: string;
    url?: string;
    filename?: string;
    size?: number;
  };
}

export interface GetConversationAttachmentsResponseDto {
  attachments?: AttachmentWrapperDto[];
  totalCount: number;
  nextPageToken?: string;
  hasMore: boolean;
}

export interface ConversationAttachmentsParams {
  conversationId: string;
  limit: number;
  pageToken?: string;
  contentType?: AttachmentType;
}

export interface ConversationAttachmentsResult {
  attachments: AttachmentWrapperDto[];
  nextPageToken?: string;
}

export const addMembers = async (conversationId: string, userIds: string[]): Promise<MemberAddResponse> => {
  const response = await http.post<ResultApi<MemberAddResponse>>(
    CONVERSATION_ITEM_MEMBER.replace(':conversationId', conversationId),
    {
      userIds
    }
  );

  return response.data.data!;
};

export const getConversations = async ({
  pageToken,
  pageSize = 20,
  search
}: ConversationsParams): Promise<ConversationListResponse> => {
  const params = new URLSearchParams({
    PageSize: pageSize.toString()
  });

  if (pageToken) {
    params.append('PageToken', pageToken);
  }

  if (search) {
    params.append('search', search);
  }

  const { data } = await http.get<ResultApi<ConversationListResponse>>(`${CONVERSATION}?${params.toString()}`);

  const userIds = data.data?.conversations
    .filter((c) => c.conversationType === 'DIRECT' && c.partnerId)
    .map((c) => c.partnerId!);

  const users = await getUserByIds(userIds || [], true);

  data.data?.conversations.forEach((c) => {
    if (c.conversationType === 'DIRECT' && c.partnerId) {
      c.conversationAvatarRef = users.find((u) => u.id === c.partnerId)?.profile.avatarRef || undefined;
    }
  });

  if (!data.data) {
    throw new Error('No data received from API');
  }

  return {
    conversations: data.data.conversations || [],
    nextPageToken: data.data.nextPageToken,
    pageSize: data.data.pageSize
  };
};

// Tạo cuộc trò chuyện mới
export const createConversation = async (data: ConversationCreate): Promise<Conversation> => {
  if (data.file) {
    const path = await uploadAsync(data.file);
    data.avatarRef = path;
  }
  const response = await http.post<ResultApi<Conversation>>(CONVERSATION, data);
  return response.data.data!;
};

export const leaveConversation = async (id: string): Promise<Conversation> => {
  const response = await http.post<ResultApi<Conversation>>(CONVERSATION_ITEM_LEAVE.replace(':conversationId', id));
  if (!response.data.data) {
    throw new Error('Failed to leave conversation');
  }
  return response.data.data;
};

//
export const deleteConversation = async (convId: string) => {
  return http.delete(CONVERSATION_ITEM.replace(':conversationId', convId));
};

export const updateConversation = async (
  conversationId: string,
  data: Conversation
): Promise<UpdateConversationRes> => {
  const response = await http.put<ResultApi<UpdateConversationRes>>(
    CONVERSATION_ITEM.replace(':conversationId', conversationId),
    data.toUpdateModel()
  );

  if (!response.data.data) {
    throw new Error('Failed to update conversation');
  }

  return response.data.data;
};

export const getConversationAttachments = async (
  params: ConversationAttachmentsParams
): Promise<ConversationAttachmentsResult> => {
  const { conversationId, limit, pageToken, contentType } = params;

  const searchParams = new URLSearchParams({
    Limit: String(limit)
  });

  if (pageToken) searchParams.append('PaginationToken', pageToken);
  if (contentType) searchParams.append('contentType', contentType);

  const { data } = await http.get<ResultApi<GetConversationAttachmentsResponseDto>>(
    `${CONVERSATION_ITEM_ATTACHMENTS.replace(':conversationId', conversationId)}?${searchParams.toString()}`
  );

  if (!data.data) {
    return { attachments: [], nextPageToken: undefined };
  }

  return {
    attachments: data.data.attachments || [],
    nextPageToken: data.data.nextPageToken || undefined
  };
};

// Search conversations using Search Service API
export interface SearchConversationsParams {
  query: string;
  page: number;
  perPage?: number;
}

/**
 * Search conversations using remote full-text search API
 *
 * **Endpoint:** GET /v1/search/conversations
 *
 * **Authentication:** Automatically includes X-User-Id header from auth store
 *
 * **Features:**
 * - Full-text search across conversation names
 * - Paginated results (default 20 per page)
 * - Returns standardized search response with totalCount
 *
 * @param params - Search parameters
 * @param params.query - Search term
 * @param params.page - Page number (1-indexed)
 * @param params.perPage - Items per page (default: 20)
 * @returns Promise with search results
 * @throws Error if user not authenticated or no results received
 *
 * @example
 * ```typescript
 * const results = await searchConversations({
 *   query: 'team meeting',
 *   page: 1,
 *   perPage: 20
 * });
 * console.log(results.totalCount, results.items);
 * ```
 */
export const searchConversations = async ({
  query,
  page,
  perPage = 20
}: SearchConversationsParams): Promise<ConversationSearchResponse> => {
  const userId = useAuthStore.getState().user?.user_id;

  if (!userId) {
    throw new Error('User not authenticated');
  }

  const params = new URLSearchParams({
    query,
    page: page.toString(),
    perPage: perPage.toString()
  });

  const { data } = await http.get<ResultApi<ConversationSearchResponse>>(
    `${SEARCH_CONVERSATIONS}?${params.toString()}`
  );

  if (!data.data) {
    throw new Error('No search results received');
  }

  return data.data;
};

/**
 * Search messages within a conversation
 *
 * **Endpoint:** GET /search-service/api/v1/search/messages
 *
 * **Authentication:** Automatically includes X-User-Id header from auth store
 *
 * **Features:**
 * - Full-text search within specific conversation
 * - Paginated results (default 20 per page)
 * - Returns message content, sender, timestamp, bucketId
 *
 * @param params - Search parameters
 * @param params.conversationId - Target conversation ID
 * @param params.query - Search term (min 3 chars recommended)
 * @param params.page - Page number (1-indexed)
 * @param params.perPage - Items per page (default: 20)
 * @returns Promise with search results
 * @throws Error if user not authenticated or no results received
 *
 * @example
 * ```typescript
 * const results = await searchMessages({
 *   conversationId: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
 *   query: 'important',
 *   page: 1,
 *   perPage: 20
 * });
 * console.log(results.totalCount, results.items);
 * ```
 */
export const searchMessages = async ({
  conversationId,
  query,
  page,
  perPage = 20
}: MessageSearchParams): Promise<MessageSearchResponse> => {
  const userId = useAuthStore.getState().user?.user_id;

  if (!userId) {
    throw new Error('User not authenticated');
  }

  const params = new URLSearchParams({
    conversationId,
    query,
    page: page.toString(),
    perPage: perPage.toString()
  });

  const { data } = await http.get<ResultApi<MessageSearchResponse>>(`${SEARCH_MESSAGES}?${params.toString()}`);

  if (!data.data) {
    throw new Error('No search results received');
  }

  return data.data;
};

/**
 * Get conversation details by ID
 *
 * **Endpoint:** GET /v1/conversation/:conversationId
 *
 * @param conversationId - Conversation ID
 * @returns Promise with conversation details
 * @throws Error if conversation not found
 *
 * @example
 * ```typescript
 * const conversation = await getConversationById('3fa85f64-5717-4562-b3fc-2c963f66afa6');
 * ```
 */
export const getConversationById = async (conversationId: string): Promise<Conversation> => {
  const { data } = await http.get<ResultApi<Conversation>>(
    CONVERSATION_ITEM.replace(':conversationId', conversationId)
  );

  if (!data.data) {
    throw new Error('Conversation not found');
  }

  return data.data;
};
